// SIMS-9072 (PRD v0.3 FR-13/FR-14, decision D6) — warn when a digital fishbowl has no
// reachable zero-value prize tier, because then EVERY draw wins.
//
// DELIBERATELY NON-BLOCKING. D6: a protocol may legitimately want an all-win draw, and making
// the zero tier mandatory only when the game presentation is selected would turn a previously
// valid configuration invalid by flipping a presentation switch. So this is a warning the
// configurator reads, never a gate — which is why it lives here and NOT in the save path
// alongside the SIMS-6263 and SIMS-9071 rules.
//
// WHY "REACHABLE" RATHER THAN "PRESENT". AC2 is worded as "prize tiers including a zero-value
// tier", but the PO was asked about the edge case directly (portal Q-4) and chose option B: the
// warning is about whether a participant can actually lose, not about whether a row exists. A
// tier of { amount: 0, probability: 0 } is configured but can never be drawn, so every draw
// still wins and the warning must STAY. This is a recorded, instructed departure from AC2's
// literal wording.
//
// Running example: A $0.00/50%, B $5.00/35%, C $12.75/15% -> no warning, A is reachable.
//                  Delete A, leaving B 70% / C 30%        -> warning, save still allowed.
//                  A $0.00 at 0%, B $5.00 at 100%         -> warning (Q-4/B): A can never be drawn.

/**
 * The copy FR-13 asks for. Names no currency and no amount: SIMS-6215 labels TOKEN amounts
 * "pts" rather than "$", and this warning has to read correctly on a TOKEN programme too.
 */
export const NO_ZERO_TIER_WARNING =
	"No losing outcome is configured, so every draw will win. This configuration can still be saved.";

interface PrizeTierLike {
	amount?: unknown;
	probability?: unknown;
}

/**
 * Whether this tier is a losing outcome a participant can actually land on.
 *
 * Both halves are required by Q-4/B: it must pay nothing AND be drawable. A probability that is
 * absent or not a number is treated as not drawable — mid-edit the field is momentarily empty,
 * and a warning that blinks off while someone is typing a probability is worse than one that
 * stays until the value is real.
 */
export function isReachableZeroTier(tier?: PrizeTierLike | null): boolean {
	const amount = Number(tier?.amount);
	const probability = Number(tier?.probability);
	return (
		Number.isFinite(amount) &&
		amount === 0 &&
		Number.isFinite(probability) &&
		probability > 0
	);
}

/**
 * AC1/AC2 — whether to show the warning.
 *
 * AC3 is satisfied BY CONSTRUCTION: this function takes no presentation input, so the warning
 * cannot behave differently under the list and the game. That is the point of the AC — it is
 * what stops the warning becoming a de-facto block when the game is selected.
 *
 * An absent or empty tier list shows nothing. "Not configured yet" is not "every draw will win",
 * and the same skip is deliberate in the SIMS-6263 and SIMS-9071 rules next door.
 */
export function shouldWarnNoZeroTier(tiers?: PrizeTierLike[] | null): boolean {
	if (!Array.isArray(tiers) || tiers.length === 0) {
		return false;
	}
	return !tiers.some(isReachableZeroTier);
}
